import React, { useEffect, useState } from "react"

import { ReactComponent as IconSearch } from "@common_assets/svg/search.svg"
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material"
import { DateValidationError } from "@mui/x-date-pickers/models"
import { StudentSavingsTypes } from "api/reactQuery/queries/savings.types"
import { useQueryAccountTransactions } from "api/reactQuery/queries/transaction"
import { IAccountTransactions } from "api/reactQuery/queries/transaction.types"
import DatePicker from "components/common/datePicker/DatePicker"
import { getDatePickerErrorMessage } from "components/module/admin/schoolTransactions/datePicker/validaiton"
import dayjs from "dayjs"
import { useAppSelector } from "hooks/store"
import { useTranslation } from "react-i18next"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { selectAccount } from "store/savings/savings.selectors"
import { AccountTransactionType } from "ts/enums/Transaction"

import { IInitialDate } from "../savingsColumn/SavingsColumn.types"
import TransactionsListing from "./transactionsListing"
import { transactionsTypeOptions } from "./transactionsListing/TransactionListing.utility"

interface ITransactionsHistory {
  onClickLoadMore: () => void
  initialData: () => IInitialDate
  propsTransactionsHistory: () => {
    typeValue: string
    isDateDisplay: string
    startDate: string | null
    endDate: string | null
    searchWithPage: { page: number; value: string }
  }
}

const TransactionsHistory = ({
  onClickLoadMore,
  initialData,
  propsTransactionsHistory,
}: ITransactionsHistory) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [focused, setFocused] = useState(false)
  const [currentValue, setCurrentValue] = useState("")
  const [accountTransferData, setAccountTransferData] = useState<
    IAccountTransactions[]
  >([])
  const [isApplied, setIsApplied] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorMessageStart, setErrorMessageStart] = useState("")
  const [errorMessageEnd, setErrorMessageEnd] = useState("")
  const [isStartBlurred, setIsStartBlurred] = useState(false)
  const [isEndBlurred, setIsEndBlurred] = useState(false)
  const [startDateValue, setStartDateValue] = useState<ReturnType<
    typeof dayjs
  > | null>(null)
  const [endDateValue, setEndDateValue] = useState<ReturnType<
    typeof dayjs
  > | null>(null)

  const classData = useAppSelector(selectAccount)

  const filteredTransactionType = (classData: StudentSavingsTypes | null) => {
    if (!classData) {
      return transactionsTypeOptions.filter((type) => type !== "Interest")
    } else {
      return transactionsTypeOptions.filter(
        (type) => type === "Interest" || type === "Transfer",
      )
    }
  }

  const isFetching = (
    start: string | null,
    end: string | null,
    isDisplay: string,
  ) => {
    return (
      isDisplay === "all" ||
      (isDisplay === "select" &&
        start !== null &&
        end !== null &&
        isApplied &&
        !isError)
    )
  }

  const {
    setTypeValue,
    setIsDateDisplay,
    setStartDate,
    setEndDate,
    setSearchWithPage,
  } = initialData()

  const { typeValue, isDateDisplay, startDate, endDate, searchWithPage } =
    propsTransactionsHistory()

  const parsedDate = (date: string | null) => {
    if (!date) return undefined

    const parsed = dayjs(date)
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD")
    }
    return undefined
  }

  const {
    data,
    isLoading,
    isRefetching,
    isFetching: isFetchingNextData,
  } = useQueryAccountTransactions({
    saving_account_id: !!classData ? classData.id : undefined,
    transaction_filter: searchWithPage.value || undefined,
    transaction_type: typeValue === "all_types" ? undefined : typeValue,
    skip: searchWithPage.page,
    start_date: parsedDate(startDate),
    end_date: parsedDate(endDate),
    options: {
      staleTime: 0,
      enabled: isFetching(startDate, endDate, isDateDisplay),
      onSuccess: (data) => {
        const isRefetchAfterInvalidation =
          searchWithPage.page > 0 &&
          data?.data[0] &&
          accountTransferData.some((item) => item.id === data.data[0].id)

        if (searchWithPage.page === 0 || isRefetchAfterInvalidation) {
          setAccountTransferData(data?.data || [])
          if (isRefetchAfterInvalidation) {
            setSearchWithPage((prev) => ({ ...prev, page: 0 }))
          }
        } else {
          setAccountTransferData((prev) => [...prev, ...(data?.data || [])])
        }
      },
    },
  })

  useEffect(() => {
    if (
      isDateDisplay === "all" ||
      (isDateDisplay === "select" && startDate !== null && endDate !== null)
    ) {
      setAccountTransferData([])
    }
  }, [typeValue, classData?.id, searchWithPage.value])

  useEffect(() => {
    if (startDate === null) setStartDateValue(null)
  }, [startDate])

  useEffect(() => {
    if (endDate === null) setEndDateValue(null)
  }, [endDate])

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchWithPage({
        page: 0,
        value: currentValue,
      })
    }, 500)

    return () => clearTimeout(handler)
  }, [currentValue])

  const handleOpenSetDate = () => {
    setIsDateDisplay("all")
    setSearchWithPage((prev) => ({ ...prev, page: 0 }))
    setStartDate(null)
    setEndDate(null)
    setIsApplied(false)
    setIsError(false)
    setErrorMessageStart("")
    setErrorMessageEnd("")
    setIsStartBlurred(false)
    setIsEndBlurred(false)
    if (isDateDisplay === "all") {
      setIsDateDisplay("select")
    }
  }

  return (
    <Stack
      width="100%"
      my={2}
      sx={{
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.mockup.primary95}`,
        borderRadius: "16px",
        py: 2,
        px: 2,
      }}
    >
      <Box>
        <Typography fontSize="24px" color="mockup.neutral10">
          {t("studentProfile.transactionHistory")}
        </Typography>
        <Divider
          orientation="horizontal"
          style={{
            borderColor: theme.palette.mockup.primary95,
            marginTop: "16px",
            marginBottom: "20px",
            marginLeft: "-16px",
            marginRight: "-16px",
          }}
        />
      </Box>
      <Box>
        <Box display="flex" columnGap={1} mb={2}>
          <TextField
            sx={{
              flex: "1 0 50%",
            }}
            disabled={isError}
            label={`${t("myActivity.searchFor")}...`}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      color: theme.palette.primary.main,
                      p: 0.25,
                      ml: -1,
                      mr: 1,
                    }}
                  >
                    <IconSearch />
                  </InputAdornment>
                ),
              },
              inputLabel: {
                shrink: focused || !!currentValue,
                style: {
                  marginLeft: focused || !!currentValue ? 5 : 20,
                },
              },
            }}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          <TextField
            label={t("myActivity.transactionType")}
            select
            value={typeValue}
            onChange={(e) => {
              setTypeValue(e.target.value)
              setSearchWithPage((prev) => ({ ...prev, page: 0 }))
            }}
          >
            <MenuItem value={AccountTransactionType.ALL_TYPES}>
              {t("myActivity.allTypes")}
            </MenuItem>
            {filteredTransactionType(classData).map((transaction) => (
              <MenuItem key={transaction} value={transaction.toLowerCase()}>
                {transaction}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <Button variant="outlined" onClick={handleOpenSetDate} fullWidth>
            {t("myActivity.setDate")}
          </Button>
        </Box>
        {isDateDisplay === "select" && (
          <Box display="flex" columnGap={2} mt={2} mb={2}>
            <DatePicker
              label="Start date"
              onChange={(value) => {
                setStartDateValue(value)
                if (!value || (value.isValid() && value.year() >= 1000)) {
                  setStartDate(value ? value.format("YYYY-MM-DD") : null)
                }
                setIsApplied(false)
                setIsStartBlurred(false)
              }}
              onError={(error: DateValidationError) => {
                const message = getDatePickerErrorMessage(
                  error,
                  "start",
                  startDateValue,
                  undefined,
                  endDateValue,
                  undefined,
                  endDateValue,
                )
                setErrorMessageStart(message)
                setIsError(!!error)
              }}
              maxDate={endDateValue ?? undefined}
              value={startDateValue}
              slotProps={{
                textField: {
                  sx: { width: "max-content" },
                  error: isStartBlurred && !!errorMessageStart,
                  helperText: isStartBlurred ? errorMessageStart : "",
                  inputProps: {
                    placeholder: "MM/DD/YYYY",
                  },
                  onBlur: () => setIsStartBlurred(true),
                  onFocus: () => setIsStartBlurred(false),
                },
              }}
            />
            <DatePicker
              label="End date"
              onChange={(value) => {
                setEndDateValue(value)
                if (!value || (value.isValid() && value.year() >= 1000)) {
                  setEndDate(value ? value.format("YYYY-MM-DD") : null)
                }
                setIsApplied(false)
                setIsEndBlurred(false)
              }}
              onError={(error: DateValidationError) => {
                const message = getDatePickerErrorMessage(
                  error,
                  "end",
                  endDateValue,
                  startDateValue,
                  undefined,
                  startDateValue,
                  undefined,
                )
                setErrorMessageEnd(message)
                setIsError(!!error)
              }}
              minDate={startDateValue ?? undefined}
              value={endDateValue}
              slotProps={{
                textField: {
                  sx: { width: "max-content" },
                  error: isEndBlurred && !!errorMessageEnd,
                  helperText: isEndBlurred ? errorMessageEnd : "",
                  inputProps: {
                    placeholder: "MM/DD/YYYY",
                  },
                  onBlur: () => setIsEndBlurred(true),
                  onFocus: () => setIsEndBlurred(false),
                },
              }}
            />
            <Button
              disabled={!startDate || !endDate || isError}
              onClick={() => {
                setAccountTransferData([])
                setIsApplied(true)
                setSearchWithPage((prev) => ({ ...prev, page: 0 }))
              }}
              sx={{ alignSelf: "center" }}
            >
              {t("myActivity.apply")}
            </Button>
          </Box>
        )}
        {!accountTransferData.length && (isLoading || isFetchingNextData) && (
          <span style={{ display: "block", textAlign: "center" }}>
            <CircularProgress />
          </span>
        )}
        <Stack rowGap={2}>
          <Box>
            <TransitionGroup component={null}>
              {accountTransferData.map((transaction) => (
                <CSSTransition
                  key={transaction.id}
                  timeout={700}
                  classNames="animation-fade"
                >
                  <TransactionsListing
                    data={transaction}
                    isSavingAccount={!!classData}
                  />
                </CSSTransition>
              ))}
            </TransitionGroup>
          </Box>
          {!!accountTransferData.length && isFetchingNextData && (
            <span style={{ display: "block", textAlign: "center" }}>
              <CircularProgress />
            </span>
          )}
          {!accountTransferData.length && !isLoading && !isRefetching && (
            <Stack justifyContent="center" alignItems="center" p={2}>
              <Typography variant="subtitle1" fontWeight="600">
                {t("myActivity.noTransactionsDuringThisDateRange")}
              </Typography>
            </Stack>
          )}
          {data?.data.length === 10 && !isLoading && !isRefetching && (
            <Button variant="text" onClick={onClickLoadMore}>
              {t("myActivity.loadMore")}
            </Button>
          )}
        </Stack>
      </Box>
    </Stack>
  )
}
export default TransactionsHistory
